<template>
  <div class="order-status-progress" :class="{ 'order-status-progress--cancelled': isCancelled }">
    <div class="status-title">
      {{ progressStatusText }}
    </div>

    <div v-if="!isCancelled" class="progress-stepper">
      <template v-for="(step, index) in steps" :key="step.type">
        <div v-if="index > 0" class="progress-stepper__line" :class="{ active: step.active }" />
        <div class="progress-stepper__step">
          <OrderProgressIcon :type="step.type" :active="step.active" />
          <span class="progress-stepper__label" :class="{ active: step.active }">
            {{ step.label }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import OrderProgressIcon from './OrderProgressIcon.vue'
import {
  getProgressStatusText,
  isOrderPaidOrCompleted,
  isStatusIconActive,
  ORDER_STATUSES,
} from '@entities/order'
import type { Order } from '@entities/order'

interface Props {
  order: Order | null
}

const props = defineProps<Props>()

/**
 * Получает тип заказа (поддерживает snake_case и camelCase)
 */
const orderType = computed(() => {
  return props.order?.order_type || props.order?.orderType
})

/**
 * Проверяет, нужно ли показывать иконку доставки
 * Показываем если тип заказа - доставка ИЛИ статус - в доставке/выполнен
 */
const shouldShowDeliveryIcon = computed(() => {
  const type = orderType.value
  const status = props.order?.status

  // Тип заказа - доставка, либо статус - в доставке/выполнен (независимо от типа)
  return type === 'delivery' || status === 'delivery' || status === 'completed'
})

const isCancelled = computed(() => props.order?.status === ORDER_STATUSES.CANCELLED)

/**
 * Шаги прогресса: иконка + подпись + активность
 */
const steps = computed(() => {
  const result: { type: 'hourglass' | 'check' | 'bag' | 'delivery' | 'completed'; label: string }[] = []

  if (!isOrderPaidOrCompleted(props.order)) {
    result.push({ type: 'hourglass', label: 'Оплата' })
  }
  result.push({ type: 'check', label: 'Оплачен' }, { type: 'bag', label: 'Готовим' })
  if (shouldShowDeliveryIcon.value) {
    result.push({ type: 'delivery', label: 'Доставка' })
  }
  result.push({ type: 'completed', label: 'Готово' })

  return result.map(step => ({
    ...step,
    active: isStatusIconActive(props.order, step.type),
  }))
})

/**
 * Получает текст статуса для отображения в прогрессе
 */
const progressStatusText = computed(() => {
  return getProgressStatusText(props.order)
})
</script>

<style scoped lang="scss">
.order-status-progress {
  @apply bg-hui-content2 rounded-hui-lg py-5 px-4 text-center;

  &--cancelled .status-title {
    @apply text-hui-danger;
  }
}

.status-title {
  @apply text-lg font-semibold mb-4 text-hui-foreground;
}

.progress-stepper {
  @apply flex items-start justify-center;

  &__step {
    @apply flex flex-col items-center gap-1.5 shrink-0;
  }

  &__line {
    // Центр круга = 20px (h-10), линия выравнивается по нему
    @apply flex-1 max-w-12 h-0.5 mt-[19px] mx-1 rounded-full bg-hui-default-200 transition-colors;

    &.active {
      @apply bg-hui-warning;
    }
  }

  &__label {
    @apply text-xs text-hui-default-400 transition-colors;

    &.active {
      @apply text-hui-foreground font-medium;
    }
  }
}
</style>